import { isNil, uniq } from "lodash";
import {
  getCheckboxBase,
  getChildLoopGroupKey,
  getChildLoopIndex,
  hasSource,
  isCheckboxAnchor,
  isDentalMapping,
  isSkipAnchor,
  isStaticMapping,
} from "./anchors";
import { defaultFieldMapping, fieldOfZone } from "./data-fields";

export const ZONE_TYPES = {
  ANCHOR: "anchor",
  CHECKBOX_GROUP: "checkboxGroup",
  CHILD_LOOP: "childLoop",
  SIGNATURE: "signature",
};

const byCheckboxOrder = (a, b) => a.length - b.length || a.localeCompare(b);

const byChildLoopIndex = (a, b) => getChildLoopIndex(a) - getChildLoopIndex(b);

const toZone = (anchor, anchors) => {
  if (isCheckboxAnchor(anchor)) {
    const base = getCheckboxBase(anchor);
    return {
      id: `[${base}]`,
      type: ZONE_TYPES.CHECKBOX_GROUP,
      key: null,
      anchors: anchors
        .filter(
          (candidate) =>
            isCheckboxAnchor(candidate) && getCheckboxBase(candidate) === base,
        )
        .sort(byCheckboxOrder),
    };
  }
  const groupKey = getChildLoopGroupKey(anchor);
  if (groupKey)
    return {
      id: groupKey,
      type: ZONE_TYPES.CHILD_LOOP,
      key: groupKey,
      anchors: anchors
        .filter((candidate) => getChildLoopGroupKey(candidate) === groupKey)
        .sort(byChildLoopIndex),
    };
  return {
    id: anchor,
    type: isSkipAnchor(anchor) ? ZONE_TYPES.SIGNATURE : ZONE_TYPES.ANCHOR,
    key: anchor,
    anchors: [anchor],
  };
};

export const zonesOf = (anchors = []) => {
  const unique = uniq(anchors);
  const seen = new Set();
  const zones = [];
  for (const anchor of unique) {
    const zone = toZone(anchor, unique);
    if (seen.has(zone.id)) continue;
    seen.add(zone.id);
    zones.push(zone);
  }
  return zones;
};

export const isAnchorSettled = (anchor, mappings = {}) => {
  if (isCheckboxAnchor(anchor)) return true;
  if (isSkipAnchor(anchor)) return true;
  const groupKey = getChildLoopGroupKey(anchor);
  if (groupKey) return hasSource(mappings[groupKey]);
  const mapping = mappings[anchor];
  if (isStaticMapping(mapping)) return true;
  if (isDentalMapping(mapping)) return hasSource(mapping) && !!mapping.schema;
  return hasSource(mapping);
};

export const isZoneSettled = (zone, mappings = {}) =>
  zone.anchors.every((anchor) => isAnchorSettled(anchor, mappings));

export const settledZones = (anchors, mappings) =>
  zonesOf(anchors).filter((zone) => isZoneSettled(zone, mappings));

export const unsettledZones = (anchors, mappings) =>
  zonesOf(anchors).filter((zone) => !isZoneSettled(zone, mappings));

export const readiness = (anchors, mappings) => {
  const zones = zonesOf(anchors);
  return {
    ready: zones.filter((zone) => isZoneSettled(zone, mappings)).length,
    total: zones.length,
  };
};

export const autoMappings = (anchors) => {
  const mappings = {};
  for (const zone of zonesOf(anchors)) {
    if (zone.type !== ZONE_TYPES.ANCHOR) continue;
    const field = fieldOfZone(zone.id);
    if (isNil(field)) continue;
    mappings[zone.key] = defaultFieldMapping(field);
  }
  return mappings;
};
